import { useEffect, useMemo, useRef, useState } from 'react'
import { TableCellsIcon } from '@heroicons/react/24/outline'
import { useDispatch } from 'react-redux'
import { useSelector } from '../../../Flux'
import type { State } from '../../../Flux/Slice'
import { Actions } from '../../../Flux/Slice'
import { projectSlice } from '../../../Flux/Slice/Project/ProjectSlice.js'
import { SelectableGroupingWrapper } from '../../Component/SampleGrouping/UI/SelectableGroupingWrapper.js'
import type { SampleBucket } from '../../Component/SampleGrouping/OperationTypes.js'
import type { VisualizationPipeline } from '../../Component/SampleGrouping/RunPipeline.js'
import type { VisualizationSampleContainer } from '../../Component/SampleGrouping/VisualizationSampleContainer.js'
import type { ProjectOrder } from '../../../Model/Project/IProjectSample.js'
import { useDismissable } from '../../../Framework/View/Hooks/useDismissable.ts'
import { SampleColumnPicker } from './SampleColumnPicker.js'
import { SAMPLE_COLUMN_LIMIT, commonLabelPrefix, sampleColumnWindow } from './SampleColumnWindow.js'

/** One inline read column: a sample or a merged sample group; `headerLabel` is `label` without the columns' common prefix. */
export type ReadsColumn = { key: string; label: string; headerLabel?: string; sampleIds: string[] }

/** URL-persistable grouping: the sample-grouping pipeline + which buckets are selected. */
export type ReadsGrouping = { pipeline: VisualizationPipeline; selectedKeys: Record<string, true> }

const DEFAULT_PIPELINE: VisualizationPipeline = { filters: [], groupBys: [], projections: [], sortBys: [] }

const isDefaultPipeline = (p: VisualizationPipeline): boolean =>
  (p.filters?.length ?? 0) === 0 && (p.groupBys?.length ?? 0) === 0 && (p.sortBys?.length ?? 0) === 0

type Props = {
  type: 'PROJECT' | 'ORDER'
  scopeId: string
  /** The samples the read matrix has, in column order. */
  sampleIds: string[]
  status: 'loading' | 'ready' | 'error'
  onColumnsChange: (columns: ReadsColumn[]) => void
  /** Restored-from-URL grouping (applied once on mount). */
  initialGrouping?: ReadsGrouping | null
  /** Lifted whenever the user changes grouping/filtering, so the parent can persist it to the URL. */
  onGroupingChange?: (grouping: ReadsGrouping | null) => void
  /** Restored-from-URL picked columns past {@link SAMPLE_COLUMN_LIMIT}, as compact keys (applied once on mount). */
  initialPicks?: string[] | null
  /** Lifted whenever the user picks columns, so the parent can persist them to the URL. */
  onPicksChange?: (picks: string[] | null) => void
  /** Whether the read columns are on; while off the menu holds just the switch, and loads nothing. */
  enabled: boolean
  onToggle: () => void
  relative: boolean
  onRelativeChange: (relative: boolean) => void
}

type WrapperState = { pipeline: VisualizationPipeline; selectedBuckets: SampleBucket[]; selectedKeys: Record<string, true> }

/**
 * Toolbar menu for the OTU table's inline per-sample read columns, whichever matrix fills them: the on/off switch and
 * every setting of the columns live in one popover, so turning them on never reflows the toolbar. By default it lifts one
 * column PER SAMPLE (labels from the sample's customer name); a slide-in overlay hosts the shared
 * {@link SelectableGroupingWrapper} so the user can merge samples into groups / filter them, at which point the
 * columns become those groups. The pipeline+selection is the ground truth and is lifted to the parent for URL
 * persistence. The wrapper stays mounted (overlay just hides it) so a restored-from-URL grouping applies without the
 * user having to open the overlay.
 */
export function SampleReadsMenu({
  type,
  scopeId,
  sampleIds,
  status,
  onColumnsChange,
  initialGrouping,
  onGroupingChange,
  initialPicks,
  onPicksChange,
  enabled,
  onToggle,
  relative,
  onRelativeChange
}: Props) {
  const dispatch = useDispatch()

  const currentSamples = useSelector((s: typeof State) => s.project.currentSamples)
  const projectOrders = useSelector((s: typeof State) => s.project.projectOrders)
  const order = useSelector((s: typeof State) => (s.order?.order === 'LOADING' ? undefined : s.order?.order))
  const samplePools = useSelector((s: typeof State) => s.order?.samplePools)

  // The OTU table route has no sample loader — load the scope's samples/orders ourselves (for grouping + labels).
  useEffect(() => {
    if (!enabled) return
    if (type === 'PROJECT') {
      if (currentSamples === 'LOADING') dispatch(Actions[projectSlice.name].getProjectSamplesRequest({ projectId: scopeId }))
      if (projectOrders === 'LOADING') dispatch(Actions[projectSlice.name].loadProjectOrdersRequest({ projectId: scopeId }))
    } else {
      // ORDER: pull the order + its sample pools (samples + metadata names) if not already loaded for this order.
      const stale = !order || order.id !== scopeId
      if (samplePools === 'LOADING' || stale) dispatch(Actions.order.getAllDatasetRequest({ orderId: scopeId }))
      if (stale) dispatch(Actions.order.getOrderRequest({ orderId: scopeId }))
    }
  }, [type, scopeId, enabled])

  // VisualizationSampleContainer[] for the grouping engine (same mapping as SelectVisualizationSamples).
  const samples: VisualizationSampleContainer[] = useMemo(() => {
    if (type === 'PROJECT') {
      if (currentSamples === 'LOADING' || projectOrders === 'LOADING') return []
      const byId: Record<string, ProjectOrder> = {}
      for (const o of projectOrders) byId[o.anonymousOrder.id] = o
      return currentSamples.map((ps) => {
        const full = ps.sample
        const po = full.orderId ? byId[full.orderId] : undefined
        return { ...full, projectSample: ps, baseOrder: po?.anonymousOrder, order: po?.fullOrder }
      })
    }
    if (!order || samplePools === 'LOADING' || !Array.isArray(samplePools)) return []
    return samplePools.flatMap((pool) => pool.samples.map((s) => ({ ...s, baseOrder: order, order })))
  }, [type, currentSamples, projectOrders, order, samplePools])

  // sampleId → display name. customerSampleName lives on the metadata (preferred) or the sample itself.
  const nameOf = useMemo(() => {
    const m = new Map<string, string>()
    for (const s of samples) {
      const name = s.sampleMetadata?.customerSampleName ?? s.customerSampleName
      if (name) m.set(s.id, name)
    }
    return m
  }, [samples])

  const [overlayOpen, setOverlayOpen] = useState<boolean>(false)
  const [menuOpen, setMenuOpen] = useState<boolean>(false)
  const menuRef = useRef<HTMLDivElement>(null)
  useDismissable(menuRef, menuOpen, () => setMenuOpen(false))

  // Grouping ground truth. `initial` seeds the always-mounted wrapper (once); `wrapperKey` forces a
  // remount on reset so the wrapper picks up the default pipeline again.
  const [initial, setInitial] = useState<ReadsGrouping | null>(() => initialGrouping ?? null)
  const [wrapperKey, setWrapperKey] = useState<number>(0)
  const [wrapperState, setWrapperState] = useState<WrapperState>({
    pipeline: initialGrouping?.pipeline ?? DEFAULT_PIPELINE,
    selectedBuckets: [],
    selectedKeys: initialGrouping?.selectedKeys ?? {}
  })

  const groupingActive = !isDefaultPipeline(wrapperState.pipeline)

  // Columns are pipeline-driven (the ground truth). No group-by ⇒ one column per sample (optionally narrowed
  // by a filter/selection); with a group-by ⇒ one column per selected bucket.
  const columns: ReadsColumn[] = useMemo(() => {
    const { pipeline, selectedBuckets } = wrapperState
    const hasGroupBy = (pipeline.groupBys?.length ?? 0) > 0
    if (hasGroupBy && selectedBuckets.length > 0) {
      return selectedBuckets.map((b) => ({ key: b.key, label: b.label, sampleIds: b.samples.map((s) => s.id) }))
    }
    // Per-sample. If the user filtered/selected without grouping, narrow to those samples.
    const allowed = !hasGroupBy && selectedBuckets.length > 0 ? new Set(selectedBuckets.flatMap((b) => b.samples.map((s) => s.id))) : null
    const ids = allowed ? sampleIds.filter((id) => allowed.has(id)) : sampleIds
    return ids.map((id) => ({ key: id, label: nameOf.get(id) ?? id.slice(0, 8), sampleIds: [id] }))
  }, [wrapperState, sampleIds, nameOf])

  const [picks, setPicks] = useState<string[] | null>(() => initialPicks ?? null)
  const columnWindow = useMemo(() => sampleColumnWindow(columns, picks), [columns, picks])
  const changePicks = (next: string[] | null) => {
    setPicks(next)
    onPicksChange?.(next)
  }

  const commonPrefix = useMemo(() => commonLabelPrefix(columnWindow.shown.map((c) => c.label)), [columnWindow.shown])
  const shownColumns = useMemo(
    () => (commonPrefix ? columnWindow.shown.map((c) => ({ ...c, headerLabel: c.label.slice(commonPrefix.length) })) : columnWindow.shown),
    [columnWindow.shown, commonPrefix]
  )

  useEffect(() => {
    onColumnsChange(shownColumns)
  }, [shownColumns, onColumnsChange])

  const handleWrapperChange = ({
    pipeline,
    selectedBuckets,
    selectedBucketKeys
  }: {
    pipeline: VisualizationPipeline
    selectedBuckets: SampleBucket[]
    selectedBucketKeys: Record<string, true>
  }) => {
    setWrapperState({ pipeline, selectedBuckets, selectedKeys: selectedBucketKeys })
    onGroupingChange?.(isDefaultPipeline(pipeline) ? null : { pipeline, selectedKeys: selectedBucketKeys })
  }

  const resetToPerSample = () => {
    setWrapperState({ pipeline: DEFAULT_PIPELINE, selectedBuckets: [], selectedKeys: {} })
    setInitial(null)
    setWrapperKey((k) => k + 1)
    onGroupingChange?.(null)
  }

  return (
    <>
      <div ref={menuRef} className="relative">
        <button
          data-testid="otu-reads-menu"
          className="btn btn-sm btn-outline"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((o) => !o)}
        >
          <TableCellsIcon className="h-5 w-5" />
          <span className="ml-1 whitespace-nowrap">Reads per sample</span>
          <span
            data-testid="otu-reads-indicator"
            aria-label={enabled ? 'on' : 'off'}
            className={`ml-1 h-2 w-2 rounded-full ${enabled ? 'bg-primary' : 'border border-current opacity-40'}`}
          />
        </button>
        {menuOpen && (
          <div className="absolute right-0 mt-2 w-80 bg-white shadow-lg rounded-lg z-50 p-2 text-sm font-normal">
            <label className="flex cursor-pointer items-center justify-between gap-2 border-b pb-2 mb-2 font-bold">
              Show reads per sample
              <input
                type="checkbox"
                data-testid="otu-reads-toggle"
                className="toggle toggle-sm toggle-primary"
                checked={enabled}
                onChange={onToggle}
              />
            </label>
            {!enabled ? (
              <p className="p-1 text-gray-500">Adds one column per sample, or per sample group, after the taxonomy columns.</p>
            ) : (
              <div className="flex flex-col gap-2 p-1">
                {status === 'loading' && (
                  <span className="flex items-center gap-1 text-gray-500">
                    <span className="loading loading-spinner loading-xs" /> loading reads…
                  </span>
                )}
                {status === 'error' && <span className="text-error">could not load reads</span>}
                {status === 'ready' &&
                  (columnWindow.windowed ? (
                    <SampleColumnPicker columns={columns} shown={columnWindow.shown} limit={SAMPLE_COLUMN_LIMIT} onChange={changePicks} />
                  ) : (
                    <span className="text-gray-500">{columns.length} column(s), scroll table right →</span>
                  ))}
                {status === 'ready' && commonPrefix && (
                  <span data-testid="otu-reads-common-prefix" className="text-gray-500 break-all">
                    Common prefix: <span className="font-mono">{commonPrefix}</span>
                  </span>
                )}
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    data-testid="otu-reads-relative"
                    className="checkbox checkbox-sm"
                    checked={relative}
                    onChange={() => onRelativeChange(!relative)}
                  />
                  Relative abundance (%)
                </label>
                <div className="flex items-center gap-2">
                  <button
                    data-testid="otu-reads-grouping"
                    className="btn btn-sm btn-outline"
                    onClick={() => {
                      setMenuOpen(false)
                      setOverlayOpen(true)
                    }}
                  >
                    Group / filter samples…
                  </button>
                  {groupingActive && (
                    <button className="btn btn-sm btn-ghost" onClick={resetToPerSample}>
                      Reset to per-sample
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Always mounted (only hidden) so a restored-from-URL grouping applies without opening the overlay. */}
      <div className={overlayOpen ? 'fixed inset-0 z-40' : 'hidden'} onClick={() => setOverlayOpen(false)}>
        <div className="absolute inset-0 bg-black/30" />
        <div
          className="absolute right-0 top-0 h-full w-[30rem] max-w-[95vw] overflow-y-auto bg-white p-4 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-lg font-semibold">Group / filter samples</h3>
            <button className="btn btn-sm btn-circle btn-ghost" onClick={() => setOverlayOpen(false)}>
              ✕
            </button>
          </div>
          <p className="mb-3 text-sm text-gray-500">
            Merge samples into groups and/or filter them — the read columns update to match. Clear the grouping to go back to one column per
            sample.
          </p>
          {samples.length === 0 ? (
            <p className="text-sm text-gray-500">Loading samples…</p>
          ) : (
            <SelectableGroupingWrapper
              key={wrapperKey}
              samples={samples}
              initialPipeline={initial?.pipeline}
              initialSelectedKeys={initial?.selectedKeys}
              onChange={handleWrapperChange}
            />
          )}
        </div>
      </div>
    </>
  )
}
